import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./RolesPage.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIconPack } from "../../context/IconPackContext";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { ToastService } from "../../service/toast.service";
import { postUser } from "../../helper/http";
import BlockGridInput from "../../components/BlockGridInput/BlockGridInput";

const STORAGE_KEY = "dashboard_roles_order";
const ROLES_MODULE_NAME = "Role"; 

interface Role {
  id: number;
  name: string;
  description: string;
  created_at: string;
}

export default function RolePage() {
  const { t } = useLanguage();
  const { getIcon } = useIconPack();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [blocksOrder, setBlocksOrder] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesModule, setRolesModule] = useState<any>(null);

  useEffect(() => {
    const fetchRolesModule = async () => {
      try {
        const response = await postUser("crud/list", {
          module: "module",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });

        const foundModule = response.data.find(
          (m: any) => m.table_name.toLowerCase() === ROLES_MODULE_NAME.toLowerCase()
        );

        if (foundModule) {
          setRolesModule(foundModule);
        } else {
          console.error(`Module with table_name "${ROLES_MODULE_NAME}" not found`);
          ToastService.error(t("rolesModuleNotFound") || "Модуль ролей не найден");
        }
      } catch (error) {
        console.error("Failed to fetch modules:", error);
        ToastService.error(t("errorLoadingModules") || "Ошибка загрузки модулей");
      }
    };

    fetchRolesModule();
  }, [t]);

  useEffect(() => {
    if (!rolesModule) return;

    const fetchRoles = async () => {
      try {
        setLoading(true);

        const response = await postUser("crud/list", {
          module: rolesModule.table_name,
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });

        setRoles(response.data);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        ToastService.error(t("errorLoadingRoles") || "Ошибка загрузки ролей");
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [rolesModule, t]);

  useEffect(() => {
    const savedOrder = localStorage.getItem(STORAGE_KEY);
    if (savedOrder) {
      try {
        setBlocksOrder(JSON.parse(savedOrder));
      } catch (e) {
        console.error("Failed to parse saved order:", e);
        initializeDefaultOrder();
      }
    } else {
      initializeDefaultOrder();
    }
  }, [roles]);

  const initializeDefaultOrder = () => {
    if (roles.length > 0) {
      const defaultOrder = roles.map((r) => String(r.id));
      setBlocksOrder(defaultOrder);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultOrder));
    }
  };

  const handleOrderChange = (newOrder: string[]) => {
    setBlocksOrder(newOrder);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
    window.dispatchEvent(new CustomEvent('rolesOrderChanged'));
  };

  const getOrderedRoles = () => {
    if (blocksOrder.length === 0) return roles;

    const ordered = blocksOrder
      .map((id) => roles.find((r) => String(r.id) === id))
      .filter(Boolean);

    const newRoles = roles.filter(
      (r) => !blocksOrder.includes(String(r.id))
    );

    return [...ordered, ...newRoles] as typeof roles;
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      ToastService.success(t("orderSaved") || "Порядок сохранён!");
    }
    setIsEditMode(!isEditMode);
  };

  const handleRoleClick = (roleId: number) => {
    if (rolesModule) {
      navigate(`/roles/${roleId}/edit`);
    }
  };

  const availableBlocks = roles.map((role) => ({
    id: String(role.id),
    label: role.name,
    icon: "user-shield",
  }));

  const orderedRoles = getOrderedRoles();

  if (!rolesModule && !loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>
          Модуль "{ROLES_MODULE_NAME}" не найден
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <p>{t("rolesManagement") || "Управление ролями"}</p>
        </div>
        <IconButton 
          onClick={toggleEditMode} 
          color="primary"
          className={styles.editButton}
          sx={{
            background: 'var(--primary)',
            color: 'white',
            '&:hover': {
              background: 'var(--primary)',
              opacity: 0.9,
            }
          }}
        >
          {isEditMode ? <SaveIcon /> : <EditIcon />}
        </IconButton>
      </div>

      <div className={styles.modulesSection}>
        {loading ? (
          <div className={styles.loader}>Загрузка ролей...</div>
        ) : isEditMode ? (
          <BlockGridInput
            value={blocksOrder}
            onChange={handleOrderChange}
            availableBlocks={availableBlocks}
            gridColumns={3}
            label={t("arrangeRoles") || "Расположение ролей"}
            gridClassName={styles.moduleGrid}
            blockClassName={styles.moduleCard}
            blockIconClassName={styles.moduleIcon}
            blockLabelClassName={styles.moduleContent}
          />
        ) : roles.length === 0 ? (
          <div className={styles.loader}>Роли не найдены</div>
        ) : (
          <div className={styles.moduleGrid}>
            {orderedRoles.map((role) => (
              <div
                key={role.id}
                className={styles.moduleCard}
                onClick={() => handleRoleClick(role.id)}
              >
                <div className={styles.moduleIcon}>
                  <FontAwesomeIcon icon={getIcon("user-shield")} />
                </div>
                <div className={styles.moduleContent}>
                  <h3>{role.name}</h3>
                  {role.description && (
                    <p className={styles.roleDescription}>{role.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}